import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "../lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/inventory", label: "Inventory" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/sell", label: "Sell Car" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-strong py-2 sm:py-3" : "bg-transparent py-3 sm:py-4"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 flex items-center justify-between gap-3 lg:gap-4 xl:gap-8 min-h-[120px]">
        {/* LEFT — Logo + Tagline */}
        <Link to="/" data-testid="nav-logo" className="flex items-center group shrink-0">
          <div className="flex flex-col items-start">
            <div
              className="brand-logo-img h-14 sm:h-16 lg:h-16 xl:h-20 w-[280px] sm:w-[340px] lg:w-[360px] xl:w-[440px] max-w-[72vw] group-hover:opacity-90 transition-opacity"
              role="img"
              aria-label="Faisal Car Bazar"
            />
            <div className="w-full text-center text-[10px] sm:text-[11px] lg:text-xs uppercase tracking-[0.4em] sm:tracking-[0.45em] text-[#D4AF37] mt-1 sm:mt-1.5 lg:mt-2 font-medium">
              Premium Pre-Owned
            </div>
          </div>
        </Link>

        {/* RIGHT — Nav + Phone */}
        <div className="hidden lg:flex items-center gap-3 xl:gap-6 shrink-0">
          <nav className="flex items-center gap-0.5 xl:gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-2 xl:px-3.5 py-2 text-xs xl:text-sm tracking-wide rounded-full transition-all whitespace-nowrap ${
                    isActive
                      ? "text-[#DC143C]"
                      : "text-[#F5F5DC]/85 hover:text-[#D4AF37]"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <a
            href={`tel:${BUSINESS.phone}`}
            data-testid="nav-call-button"
            className="flex items-center gap-1.5 xl:gap-2 text-xs xl:text-sm text-[#F5F5DC] hover:text-[#D4AF37] transition-colors whitespace-nowrap pl-3 xl:pl-6 border-l border-white/10"
          >
            <Phone size={14} className="text-[#D4AF37] shrink-0" />
            {BUSINESS.phone}
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden p-2 text-[#F5F5DC]"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass-strong border-t border-white/10 mt-4">
          <nav className="px-6 py-4 flex flex-col">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 text-sm tracking-wide border-b border-white/5 ${
                    isActive ? "text-[#DC143C]" : "text-[#F5F5DC]/85"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${BUSINESS.phone}`}
              className="mt-4 btn-primary justify-center"
              data-testid="mobile-call-button"
            >
              <Phone size={16} />
              {BUSINESS.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
